import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import PlanChangeConfirmModal from '../components/PlanChangeConfirmModal';
import api from '../api/api';
import '../styles/pages/PlansPage.css';

/**
 * プラン変更ページ - 完全新規デザイン
 * Tsumugiトンマナに準拠したプラン比較専用UI
 */
const PlansPage = () => {
  const { user, plan, updatePlan } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [expiringConversations, setExpiringConversations] = useState([]);
  const [showUpperPlans, setShowUpperPlans] = useState(false);
  const [billingInfo, setBillingInfo] = useState(null);

  useEffect(() => {
    document.title = 'プラン変更 | はしもと栄養パートナー';

    if (plan === 'free') {
      fetchExpiringConversations();
    }

    // 次回請求日を計算（有料プランのみ）
    if (user && plan !== 'free') {
      // lastPaymentDateがない場合は、createdAtまたは現在日付をフォールバック
      let baseDate;
      if (user.lastPaymentDate) {
        baseDate = new Date(user.lastPaymentDate);
      } else if (user.createdAt) {
        baseDate = new Date(user.createdAt);
      } else {
        // どちらもない場合は現在日付を使用
        baseDate = new Date();
      }

      const nextBilling = new Date(baseDate);
      nextBilling.setMonth(nextBilling.getMonth() + 1);

      setBillingInfo({
        nextBillingDate: nextBilling.toISOString()
      });
    } else {
      setBillingInfo(null);
    }
  }, [plan, user]);

  const fetchExpiringConversations = async () => {
    try {
      const response = await api.get('/api/tsumugi/expiring');
      if (response.data.success) {
        setExpiringConversations(response.data.expiring || []);
      }
    } catch (error) {
      console.error('削除予定会話の取得エラー:', error);
    }
  };

  const plansData = [
    {
      id: 'free',
      name: 'フリープラン',
      price: 0,
      priceLabel: '無料',
      emoji: '🌱',
      theme: 'green',
      tagline: 'まずは試してみたい方に',
      primaryUseCase: 'まずはつむぎと話してみる',
      monthlyPoints: 50,
      conversationRetention: '3日間',
      conversationNote: '3日後に自動削除',
      pointExamples: [
        'つむぎに毎日話しかけて + 週1回の栄養相談',
        'レシピ生成を週2回 + 成分チェック数回'
      ],
      costs: [
        { name: 'AI栄養相談', cost: '4pt/回' },
        { name: 'レシピ生成', cost: '4pt/回' },
        { name: '成分表スキャン', cost: '5pt/回' },
        { name: 'つむぎチャット', cost: '1pt/回' }
      ],
      features: [
        { text: '気分記録カレンダー', available: true },
        { text: '血液検査分析', available: false },
        { text: '詳細な栄養分析', available: false },
        { text: '会話履歴 永久保存', available: false }
      ]
    },
    {
      id: 'standard',
      name: 'スタンダードプラン',
      price: 480,
      priceLabel: '¥480',
      emoji: '🌿',
      theme: 'blue',
      tagline: '定期的に相談したい方に',
      primaryUseCase: 'つむぎに毎日相談できる',
      subBadge: 'まずは控えめに',
      monthlyPoints: 900,
      conversationRetention: '30日間',
      conversationNote: '30日後に自動削除',
      pointExamples: [
        '月間900pt。栄養相談・レシピ生成・血液検査分析など全機能が利用可能',
        'つむぎと毎日おしゃべり + 気になる食品の成分チェック'
      ],
      costs: [
        { name: 'AI栄養相談', cost: '4pt/回' },
        { name: 'レシピ生成', cost: '4pt/回' },
        { name: '成分表スキャン', cost: '5pt/回' },
        { name: '血液検査分析', cost: '8pt/回' },
        { name: 'つむぎチャット', cost: '1pt/回' }
      ],
      features: [
        { text: '気分記録カレンダー', available: true },
        { text: '詳細な栄養分析（約2500文字）', available: true },
        { text: '食材制限の詳細設定', available: true },
        { text: '会話履歴 永久保存', available: false }
      ]
    },
    {
      id: 'premium',
      name: 'プレミアムプラン',
      price: 980,
      priceLabel: '¥980',
      emoji: '💎',
      theme: 'premium',
      tagline: '検査結果も、ごはんも、相談したい方に',
      primaryUseCase: 'つむぎがずっとそばにいる',
      popular: true,
      monthlyPoints: 1800,
      conversationRetention: '永久保存',
      conversationNote: null,
      pointExamples: [
        '月間1,800pt。すべての機能をたっぷり使える',
        '成分チェックも気軽に + つむぎと毎日しっかり会話'
      ],
      costs: [
        { name: 'AI栄養相談', cost: '4pt/回' },
        { name: 'レシピ生成', cost: '4pt/回' },
        { name: '成分表スキャン', cost: '5pt/回' },
        { name: '血液検査分析', cost: '8pt/回' },
        { name: 'つむぎチャット', cost: '1pt/回' }
      ],
      features: [
        { text: '気分記録カレンダー', available: true },
        { text: '最高精度のAI分析（5000文字以上）', available: true },
        { text: '月次AIレポート', available: true },
        { text: '会話履歴 永久保存', available: true }
      ]
    },
    {
      id: 'premium_plus',
      name: 'プレミアムプラスプラン',
      price: 1980,
      priceLabel: '¥1,980',
      emoji: '👑',
      theme: 'gold',
      tagline: 'さらに充実したサポートが必要な方に',
      monthlyPoints: 3600,
      conversationRetention: '永久保存',
      conversationNote: null,
      isUpperPlan: true
    },
    {
      id: 'ultimate',
      name: 'アルティメットプラン',
      price: 2980,
      priceLabel: '¥2,980',
      emoji: '✨',
      theme: 'ultimate',
      tagline: '最上級の健康管理を求める方に',
      monthlyPoints: 5500,
      conversationRetention: '永久保存',
      conversationNote: null,
      isUpperPlan: true
    }
  ];

  const handlePlanSelect = (planId) => {
    if (planId === plan) return;
    setSelectedPlan(planId);
    setShowConfirmModal(true);
  };

  const handleConfirmPlanChange = async () => {
    setLoading(true);

    try {
      // First try the Pay.jp-integrated endpoint (for production with active subscriptions)
      try {
        const response = await api.post('/api/payment/change-plan', {
          plan: selectedPlan
        });

        if (response.data.success) {
          // Update token and user info
          if (response.data.token) {
            localStorage.setItem('token', response.data.token);
          }

          // Update context
          await updatePlan(selectedPlan);

          setShowConfirmModal(false);
          alert(`${response.data.message || 'プランが変更されました'}`);
          window.location.reload();
          return;
        }
      } catch (payjpError) {
        // If Pay.jp endpoint fails with 503 (not available), try fallback
        if (payjpError.response?.status === 503 || payjpError.response?.status === 400) {
          // Pay.jp not available or no subscription, trying fallback endpoint

          // Fallback to simple plan update (for test mode or cancellation)
          const fallbackResponse = await api.put('/api/auth/plan', {
            plan: selectedPlan
          });

          if (fallbackResponse.data.success) {
            // Update token and user info
            if (fallbackResponse.data.token) {
              localStorage.setItem('token', fallbackResponse.data.token);
            }

            // Update context
            await updatePlan(selectedPlan);

            setShowConfirmModal(false);
            alert('プランが変更されました');
            window.location.reload();
            return;
          }
        } else {
          // Other errors, re-throw
          throw payjpError;
        }
      }
    } catch (error) {
      console.error('Plan change error:', error);
      alert(error.response?.data?.message || 'プラン変更に失敗しました。もう一度お試しください。');
    } finally {
      setLoading(false);
      setShowConfirmModal(false);
    }
  };

  const currentPlanData = plansData.find(p => p.id === plan);

  return (
    <AppLayout hideTabNav>
      <div className="plan-compare">
        {/* ヘッダーセクション */}
        <div className="plan-compare__header">
          <div className="plan-compare__header-icon">💎</div>
          <h1 className="plan-compare__title">プラン変更</h1>
          <p className="plan-compare__subtitle">
            あなたに合ったプランを選んでください
          </p>
        </div>

        <div className="plan-compare__container">
          {/* Free会員向け削除予定警告 */}
          {plan === 'free' && expiringConversations.length > 0 && (
            <div className="plan-compare__alert">
              <div className="plan-compare__alert-icon">⚠️</div>
              <div className="plan-compare__alert-content">
                <h3 className="plan-compare__alert-title">
                  {expiringConversations.length}件の会話が間もなく削除されます
                </h3>
                <p className="plan-compare__alert-text">
                  フリープランでは会話履歴が3日間のみ保存されます。
                  スタンダードプラン以上にアップグレードすると、会話履歴が長期保存されます。
                </p>
                <ul className="plan-compare__alert-list">
                  {expiringConversations.slice(0, 3).map((conv, idx) => (
                    <li key={idx}>
                      {new Date(conv.expiresAt).toLocaleDateString('ja-JP')} 削除予定:
                      {conv.messages[0]?.content.substring(0, 30)}...
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* 現在のプラン表示 */}
          <div className="plan-compare__current">
            <div className="plan-compare__current-label">現在のプラン</div>
            <div className="plan-compare__current-name">
              <span className="plan-compare__current-emoji">{currentPlanData?.emoji}</span>
              <span>{currentPlanData?.name}</span>
            </div>
          </div>

          {/* 基本プランカード一覧 */}
          <div className="plan-compare__grid">
            {plansData.filter(p => !p.isUpperPlan).map((planItem) => (
              <div
                key={planItem.id}
                className={`plan-compare__card plan-compare__card--${planItem.theme} ${
                  planItem.id === plan ? 'plan-compare__card--active' : ''
                } ${planItem.popular ? 'plan-compare__card--popular' : ''}`}
              >
                {/* 人気バッジ */}
                {planItem.popular && (
                  <div className="plan-compare__badge">
                    <span className="plan-compare__badge-icon">🏆</span>
                    <span>もっとも選ばれています</span>
                  </div>
                )}

                {/* サブバッジ */}
                {planItem.subBadge && (
                  <div className="plan-compare__subbadge">
                    {planItem.subBadge}
                  </div>
                )}

                {/* カードヘッダー */}
                <div className="plan-compare__card-header">
                  <div className="plan-compare__card-emoji">{planItem.emoji}</div>
                  <h3 className="plan-compare__card-name">{planItem.name}</h3>
                  <p className="plan-compare__card-tagline">{planItem.tagline}</p>
                </div>

                {/* 価格 */}
                <div className="plan-compare__price">
                  <span className="plan-compare__price-amount">{planItem.priceLabel}</span>
                  <span className="plan-compare__price-period">/月</span>
                </div>

                {/* 月間ポイント */}
                <div className="plan-compare__points">
                  <div className="plan-compare__points-label">月間ポイント</div>
                  <div className="plan-compare__points-value">{planItem.monthlyPoints}pt</div>
                </div>

                {/* 主な用途 */}
                <div className="plan-compare__primary-use">
                  {planItem.primaryUseCase}
                </div>

                {/* CTAボタン */}
                <button
                  className={`plan-compare__cta ${
                    planItem.id === plan
                      ? 'plan-compare__cta--current'
                      : 'plan-compare__cta--select'
                  }`}
                  onClick={() => handlePlanSelect(planItem.id)}
                  disabled={planItem.id === plan || loading}
                >
                  {planItem.id === plan ? (
                    <>
                      <span className="plan-compare__cta-icon">✓</span>
                      現在のプラン
                    </>
                  ) : (
                    <>このプランを選ぶ</>
                  )}
                </button>

                {/* 折りたたみ詳細 */}
                <details className="plan-compare__details">
                  <summary className="plan-compare__details-summary">
                    詳しく見る
                  </summary>
                  <div className="plan-compare__details-content">
                    {/* ポイント換算例 */}
                    <div className="plan-compare__examples">
                      <div className="plan-compare__examples-title">📌 こんな使い方ができます</div>
                      <ul className="plan-compare__examples-list">
                        {planItem.pointExamples.map((example, idx) => (
                          <li key={idx} className="plan-compare__example-item">
                            {example}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* コスト表示 */}
                    <div className="plan-compare__costs">
                      <div className="plan-compare__costs-title">各機能の消費ポイント:</div>
                      <ul className="plan-compare__costs-list">
                        {planItem.costs.map((costItem, idx) => (
                          <li key={idx} className="plan-compare__cost-item">
                            <span className="plan-compare__cost-name">{costItem.name}</span>
                            <span className="plan-compare__cost-value">
                              {costItem.cost}
                              {costItem.note && <span className="plan-compare__cost-note">{costItem.note}</span>}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* その他の機能 */}
                    <div className="plan-compare__other-features">
                      <div className="plan-compare__other-features-title">その他の特典:</div>
                      <ul className="plan-compare__features">
                        {planItem.features.map((feature, idx) => (
                          <li
                            key={idx}
                            className={`plan-compare__feature ${
                              feature.available
                                ? 'plan-compare__feature--available'
                                : 'plan-compare__feature--unavailable'
                            }`}
                          >
                            <span className="plan-compare__feature-icon">
                              {feature.available ? '✓' : '✗'}
                            </span>
                            <span className="plan-compare__feature-text">{feature.text}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* 会話履歴保存期間 */}
                    <div className="plan-compare__retention">
                      <div className="plan-compare__retention-icon">💾</div>
                      <div className="plan-compare__retention-content">
                        <div className="plan-compare__retention-label">会話履歴保存</div>
                        <div className="plan-compare__retention-value">
                          {planItem.conversationRetention}
                        </div>
                        {planItem.conversationNote && (
                          <div className="plan-compare__retention-note">
                            {planItem.conversationNote}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </details>
              </div>
            ))}
          </div>

          {/* さらに上のプランアコーディオン */}
          <div className="plan-compare__accordion-wrapper">
            <button
              onClick={() => setShowUpperPlans(!showUpperPlans)}
              className="plan-compare__accordion-button"
            >
              <div className="plan-compare__accordion-button-content">
                <div className="plan-compare__accordion-button-title">
                  ✨ さらに上のプランをお探しですか？
                </div>
                <div className="plan-compare__accordion-button-subtitle">
                  より多くのご利用が必要な方向けのプランをご用意しています
                </div>
              </div>
              <div className={`plan-compare__accordion-icon ${showUpperPlans ? 'plan-compare__accordion-icon--open' : ''}`}>
                ▼
              </div>
            </button>

            {showUpperPlans && (
              <div className="plan-compare__upper-grid plan-compare__accordion-content">
                {plansData.filter(p => p.isUpperPlan).map((planItem) => (
                  <div
                    key={planItem.id}
                    className={`plan-compare__upper-card plan-compare__upper-card--${planItem.theme} ${
                      planItem.id === plan ? 'plan-compare__upper-card--active' : ''
                    }`}
                  >
                    <div className="plan-compare__upper-card-header">
                      <span className="plan-compare__upper-card-emoji">{planItem.emoji}</span>
                      <div>
                        <h3 className="plan-compare__upper-card-name">{planItem.name}</h3>
                        <p className="plan-compare__upper-card-tagline">{planItem.tagline}</p>
                      </div>
                    </div>

                    <div className="plan-compare__upper-card-info">
                      <div className="plan-compare__upper-card-price">
                        <span className="plan-compare__price-amount">{planItem.priceLabel}</span>
                        <span className="plan-compare__price-period">/月</span>
                      </div>
                      <div className="plan-compare__upper-card-points">
                        {planItem.monthlyPoints}pt/月
                      </div>
                    </div>

                    <button
                      className={`plan-compare__cta ${
                        planItem.id === plan
                          ? 'plan-compare__cta--current'
                          : 'plan-compare__cta--select'
                      }`}
                      onClick={() => handlePlanSelect(planItem.id)}
                      disabled={planItem.id === plan || loading}
                    >
                      {planItem.id === plan ? (
                        <>
                          <span className="plan-compare__cta-icon">✓</span>
                          現在のプラン
                        </>
                      ) : (
                        <>このプランを選ぶ</>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 注意事項 */}
          <div className="plan-compare__notes">
            <h3 className="plan-compare__notes-title">プラン変更について</h3>
            <ul className="plan-compare__notes-list">
              <li>
                <span className="plan-compare__notes-icon">✓</span>
                <span>プラン変更は即座に反映されます</span>
              </li>
              <li>
                <span className="plan-compare__notes-icon">✓</span>
                <span>アップグレード時は、既存の会話履歴の保存期間が自動的に延長されます</span>
              </li>
              <li>
                <span className="plan-compare__notes-icon">✓</span>
                <span>ダウングレード時は、次回更新日から新しい保存期間が適用されます</span>
              </li>
              <li>
                <span className="plan-compare__notes-icon">✓</span>
                <span>いつでもプラン変更・解約が可能です</span>
              </li>
            </ul>
          </div>

          {/* 戻るボタン */}
          <div className="plan-compare__footer">
            <button
              className="plan-compare__back-btn"
              onClick={() => navigate('/mypage')}
            >
              ← マイページに戻る
            </button>
          </div>
        </div>

        {/* プラン変更確認モーダル */}
        {showConfirmModal && (
          <PlanChangeConfirmModal
            isOpen={showConfirmModal}
            onClose={() => setShowConfirmModal(false)}
            currentPlan={plan}
            newPlan={selectedPlan}
            billingInfo={billingInfo}
            onConfirm={handleConfirmPlanChange}
          />
        )}
      </div>
    </AppLayout>
  );
};

export default PlansPage;
